import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CollectionCard } from './components/CollectionCard';
import { ConfirmRemoveCollectionModal } from './components/ConfirmRemoveCollectionModal';
import { CreateCollectionModal } from './components/CreateCollectionModal';
import { SearchFilters } from './components/SearchFilters';

import { AnimateSpin } from '@/components/AnimateSpin';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useGetMerchandisingCollections } from '@/hooks/useGetMerchandisingCollections';
import {
  MerchandisingCollectionsQueryFilterDto,
  MerchandisingCollection,
} from '@/services/MerchandisingCollectionsService';

const queryFiltersInitialState = {
  orderBy: 'createdAt',
  orderDirection: 'ASC',
} as Partial<MerchandisingCollectionsQueryFilterDto>;

export function MerchandisingCollections() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [collectionToRemove, setCollectionToRemove] =
    useState<MerchandisingCollection | null>(null);

  const [queryFilters, setQueryFilters] = useState({
    orderBy: 'createdAt',
    orderDirection: 'ASC',
  } as Partial<MerchandisingCollectionsQueryFilterDto>);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.MerchandisingCollections',
  });

  const handleOpenRemoveModal = useCallback(
    (collection: MerchandisingCollection) => {
      setCollectionToRemove(collection);
    },
    [],
  );

  const handleCloseRemoveModal = useCallback(() => {
    setCollectionToRemove(null);
  }, []);

  const handleQueryFilters = useCallback(
    (queryFilters: Partial<MerchandisingCollectionsQueryFilterDto>) => {
      setQueryFilters(queryFilters);
    },
    [],
  );

  const { merchandisingCollections, isFetching } =
    useGetMerchandisingCollections(queryFilters);

  return (
    <div className="py-8">
      <section>
        <h1 className="text-[42px] tracking-[8.25px]">{t('title')}</h1>
        <p className="text-muted-foreground">{t('description')}</p>
      </section>

      <section className="mt-10 space-y-10">
        <div>
          <SearchFilters
            queryFiltersInitialState={queryFiltersInitialState}
            onQueryFilters={handleQueryFilters}
          />
        </div>

        {merchandisingCollections.length > 0 && (
          <div className="mt-10 space-y-4">
            <Button
              className="w-full md:w-auto"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('newCollectionButtonLabel')}
            </Button>

            <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {merchandisingCollections.map((collection) => (
                <li key={collection.id}>
                  <CollectionCard
                    collection={collection}
                    onRemove={handleOpenRemoveModal}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {isFetching && (
        <div className="mt-10">
          <AnimateSpin />
        </div>
      )}

      {!isFetching && merchandisingCollections.length === 0 && (
        <section className="mt-10">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="space-y-2 text-center">
                <h3 className="text-lg font-semibold">
                  {queryFilters.name &&
                    t('collectionNotFoundWithTerm', {
                      name: queryFilters.name,
                    })}

                  {!queryFilters.name && t('collectionNotFound')}
                </h3>

                {!queryFilters.name && (
                  <p className="text-muted-foreground">
                    {t('firstCollection')}{' '}
                    <Button
                      variant="link"
                      className="p-0 font-semibold underline"
                      onClick={() => setIsCreateModalOpen(true)}
                    >
                      {t('firstCollectionButtonLabel')}
                    </Button>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <CreateCollectionModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />

      <ConfirmRemoveCollectionModal
        collectionToRemove={collectionToRemove}
        onRemove={handleCloseRemoveModal}
      />
    </div>
  );
}
